import React, { useState, useEffect } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useAuth } from '@/contexts/AuthContext';

const TOUR_COMPLETED_KEY = 'punchtm-tour-completed';

// Tour steps by role category
const COMMON_STEPS = [
  {
    target: '[data-testid="dashboard-layout"]',
    content: 'Bienvenue dans PunchTM ! Ce guide rapide va vous presenter les fonctionnalites principales de l\'application.',
    placement: 'center',
    disableBeacon: true,
    title: 'Bienvenue !',
  },
  {
    target: '[data-testid="sidebar"]',
    content: 'Le menu lateral vous donne acces a tous les modules de l\'application. Cliquez sur un element pour naviguer.',
    placement: 'right',
    title: 'Navigation',
  },
  {
    target: '[data-testid="nav-dashboard"]',
    content: 'Le Tableau de bord affiche en temps reel la presence de vos employes : qui est present, absent, en retard ou en pause.',
    placement: 'right',
    title: 'Tableau de bord',
  },
  {
    target: '[data-testid="nav-reports"]',
    content: 'Les Rapports montrent les pointages du jour. Vous pouvez valider, approuver ou rejeter les heures de vos employes.',
    placement: 'right',
    title: 'Rapports',
  },
  {
    target: '[data-testid="nav-employees"]',
    content: 'Gerez vos employes : creation, modification, import CSV, actions en masse. C\'est le centre RH de l\'application.',
    placement: 'right',
    title: 'Employes',
  },
];

const HR_STEPS = [
  {
    target: '[data-testid="nav-payroll"]',
    content: 'Le module Paie/RH gere les periodes de paie, les feuilles de temps et les exports. Validez les heures avant le verrouillage.',
    placement: 'right',
    title: 'Paie / RH',
  },
  {
    target: '[data-testid="nav-analytics"]',
    content: 'L\'Analytique fournit des graphiques et KPIs avances : taux de presence, tendances, comparaisons de periodes.',
    placement: 'right',
    title: 'Analytique',
  },
  {
    target: '[data-testid="nav-scheduling"]',
    content: 'La Planification permet d\'organiser les quarts de travail de vos employes sur un calendrier hebdomadaire.',
    placement: 'right',
    title: 'Planification',
  },
];

const ADMIN_STEPS = [
  {
    target: '[data-testid="nav-settings"]',
    content: 'Les Parametres vous permettent de configurer les regles de pointage, les seuils d\'alerte et les options de votre organisation.',
    placement: 'right',
    title: 'Parametres',
  },
];

const PLATFORM_STEPS = [
  {
    target: '[data-testid="nav-platform"]',
    content: 'La Plateforme vous donne une vue globale sur tous les tenants : supervision, diagnostic, assistant IA et journal des interventions.',
    placement: 'right',
    title: 'Platform Admin',
  },
];

const CLOSING_STEPS = [
  {
    target: '[data-testid="nav-documentation"]',
    content: 'La Documentation complete est integree dans l\'application. Consultez-la a tout moment pour des guides detailles par role.',
    placement: 'right',
    title: 'Documentation',
  },
  {
    target: '[data-testid="contextual-help-fab"]',
    content: 'Sur chaque page, ce bouton d\'aide vous redirige vers la section de documentation correspondante. N\'hesitez pas a l\'utiliser !',
    placement: 'left',
    title: 'Aide contextuelle',
  },
  {
    target: '[data-testid="theme-toggle"]',
    content: 'Activez le mode sombre pour un confort visuel optimise. Vos preferences sont sauvegardees automatiquement.',
    placement: 'top',
    title: 'Mode sombre',
  },
];

function getStepsForRole(role) {
  const steps = [...COMMON_STEPS];
  
  if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'admin'].includes(role)) {
    steps.push(...HR_STEPS);
  }
  
  if (['platform_superadmin', 'tenant_superuser', 'tenant_admin', 'admin'].includes(role)) {
    steps.push(...ADMIN_STEPS);
  }
  
  if (role === 'platform_superadmin') {
    steps.push(...PLATFORM_STEPS);
  }
  
  steps.push(...CLOSING_STEPS);
  return steps;
}

export default function GuidedTour({ onComplete }) {
  const { user } = useAuth();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    if (!user) return;
    // Auto-start desactive : la visite guidee ne se lance QUE via le bouton "Relancer la visite guidee" (sidebar).
    // Les steps restent disponibles pour un lancement manuel.
    const tourSteps = getStepsForRole(user.role);
    setSteps(tourSteps);
    // Ne demarre pas automatiquement, meme au 1er login. Utilisateur declenche via restartTour().
    const shouldStart = localStorage.getItem('punchtm-tour-should-run') === 'true';
    if (shouldStart) {
      localStorage.removeItem('punchtm-tour-should-run');
      const timer = setTimeout(() => setRun(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleCallback = (data) => {
    const { status } = data;
    // Marquer termine sur n'importe quelle fin (FINISHED, SKIPPED, ou fermeture ✕)
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || data.action === 'close') {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      onComplete?.();
    }
  };

  if (!run || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableOverlayClose
      callback={handleCallback}
      locale={{
        back: 'Retour',
        close: 'Fermer',
        last: 'Terminer',
        next: 'Suivant',
        open: 'Ouvrir',
        skip: 'Passer la visite',
      }}
      styles={{
        options: {
          primaryColor: '#1e40af',
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          textColor: '#0f172a',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
        },
        tooltip: {
          borderRadius: '12px',
          padding: '20px',
          fontSize: '14px',
        },
        tooltipTitle: {
          fontSize: '16px',
          fontWeight: '700',
          color: '#1e40af',
          marginBottom: '8px',
        },
        tooltipContent: {
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#334155',
        },
        buttonNext: {
          backgroundColor: '#1e40af',
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '13px',
          fontWeight: '600',
        },
        buttonBack: {
          color: '#64748b',
          fontSize: '13px',
        },
        buttonSkip: {
          color: '#94a3b8',
          fontSize: '12px',
        },
        spotlight: {
          borderRadius: '8px',
        },
        beacon: {
          display: 'none',
        },
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
}

// Export function to restart tour manually (used by sidebar button)
export function restartTour() {
  localStorage.removeItem(TOUR_COMPLETED_KEY);
  localStorage.setItem('punchtm-tour-should-run', 'true');
  window.location.reload();
}

export function isTourCompleted() {
  return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
}
